import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox as InboxIcon,
  Loader2,
  Link2,
  RefreshCw,
  Mail,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";

/**
 * Strike 2 — The Shadow Unibox. Centralised, campaign-isolated inbound replies.
 * Left: thread list. Right: thread detail + manual attach-to-lead override.
 */
export default function Unibox() {
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadQuery, setLeadQuery] = useState("");
  const [attaching, setAttaching] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/unibox/threads")
      .then((r) => {
        setThreads(r.data.threads || []);
        setUnread(r.data.unread || 0);
      })
      .catch(() => toast.error("Failed to load Unibox"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/leads")
      .then((r) => setLeads(r.data.leads || []))
      .catch(() => {});
  }, []);

  const openThread = async (t) => {
    setSelected(t);
    setLeadQuery(t.from_email || "");
    if (t.status === "unread") {
      try {
        await api.post(`/unibox/threads/${t.id}/mark-read`);
        setThreads((prev) =>
          prev.map((x) => (x.id === t.id ? { ...x, status: "read" } : x)),
        );
        setUnread((u) => Math.max(0, u - 1));
      } catch {
        /* ignore */
      }
    }
  };

  const filteredLeads = useMemo(() => {
    const q = leadQuery.trim().toLowerCase();
    const base = q
      ? leads.filter(
          (l) =>
            (l.email || "").toLowerCase().includes(q) ||
            `${l.first_name || ""} ${l.last_name || ""}`
              .toLowerCase()
              .includes(q),
        )
      : leads;
    return base.slice(0, 40);
  }, [leads, leadQuery]);

  const attach = async (leadId) => {
    if (!selected) return;
    setAttaching(true);
    try {
      const r = await api.post(`/unibox/threads/${selected.id}/attach`, {
        lead_id: leadId,
      });
      toast.success("Thread attached. Lead marked replied; follow-ups stopped.");
      setSelected(r.data.thread);
      setThreads((prev) =>
        prev.map((x) => (x.id === r.data.thread.id ? r.data.thread : x)),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach");
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Campaign replies
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
            Unibox
            {unread > 0 && (
              <span
                className="ml-3 align-middle text-xs font-mono bg-violet-500/15 text-violet-300 border border-violet-500/30 rounded-full px-2.5 py-1"
                data-testid="unibox-unread-count"
              >
                {unread} unread
              </span>
            )}
          </h1>
        </div>
        <button
          onClick={load}
          data-testid="unibox-refresh"
          className="flex items-center gap-2 px-3 py-2 border border-[#27272a] rounded-md text-sm hover:bg-[#161616] transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm font-mono">Loading…</div>
      ) : threads.length === 0 ? (
        <EmptyState
          icon={InboxIcon}
          title="No campaign replies yet"
          description="When a lead replies to one of your campaign emails, it lands here — and only campaign mail does. Personal mail and alerts are filtered out at the source."
        />
      ) : (
        <div className="grid md:grid-cols-12 gap-4">
          {/* Thread list */}
          <div className="md:col-span-5 border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-[#161616]">
              {threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => openThread(t)}
                  data-testid={`unibox-thread-${t.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-[#121212] transition-colors ${
                    selected?.id === t.id ? "bg-[#141414]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white truncate flex items-center gap-2">
                      {t.status === "unread" && (
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                      )}
                      {t.from_name || t.from_email || "Unknown sender"}
                    </span>
                    {t.status === "attached" && (
                      <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-emerald-400 border border-emerald-400/30 rounded px-1.5 py-0.5">
                        attached
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-400 truncate mt-0.5">
                    {t.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-neutral-600 truncate mt-0.5 font-mono">
                    {t.snippet}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="md:col-span-7 border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5">
            {!selected ? (
              <div className="text-sm text-neutral-500 font-mono py-16 text-center">
                Select a reply to read it.
              </div>
            ) : (
              <div className="space-y-4" data-testid="unibox-detail">
                <div>
                  <div className="text-lg text-white font-medium">
                    {selected.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-neutral-400 font-mono mt-1 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
                    {selected.from_name
                      ? `${selected.from_name} <${selected.from_email}>`
                      : selected.from_email}
                  </div>
                  {selected.matched_by && (
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-600 mt-1">
                      matched by {selected.matched_by}
                    </div>
                  )}
                </div>
                <div className="border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-4 text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
                  {selected.body || selected.snippet || "(no content captured)"}
                </div>

                {/* Attach override */}
                <div className="border-t border-[#1a1a1a] pt-4">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-2">
                    <Link2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {selected.status === "attached"
                      ? "Attached to lead"
                      : "Attach this thread to a lead"}
                  </div>
                  {selected.status === "attached" ? (
                    <div className="text-sm text-emerald-300 font-mono">
                      Lead {selected.lead_id} — marked replied, follow-ups stopped.
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          value={leadQuery}
                          onChange={(e) => setLeadQuery(e.target.value)}
                          placeholder="Search a lead by email or name…"
                          data-testid="unibox-lead-search"
                          className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md pl-9 pr-3 py-2 text-sm"
                        />
                      </div>
                      <div className="mt-2 max-h-44 overflow-y-auto border border-[#1f1f1f] rounded-md divide-y divide-[#161616]">
                        {filteredLeads.length === 0 ? (
                          <div className="text-xs text-neutral-500 font-mono px-3 py-3">
                            No matching leads.
                          </div>
                        ) : (
                          filteredLeads.map((l) => (
                            <button
                              key={l.id}
                              onClick={() => attach(l.id)}
                              disabled={attaching}
                              data-testid={`unibox-attach-lead-${l.id}`}
                              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[#141414] disabled:opacity-50"
                            >
                              <span className="truncate">
                                <span className="text-white">
                                  {l.first_name || l.email}
                                </span>{" "}
                                <span className="text-neutral-500 font-mono text-xs">
                                  {l.email}
                                </span>
                              </span>
                              {attaching ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Link2 className="w-3.5 h-3.5 text-neutral-500" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
